// src/pages/AdminCategoriasTicket.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotificationContext } from '../contexts/NotificationContext';
import { categoriasTicketAPI, authAPI } from '../services/api';
import { Shield, Edit2, Trash2, Tag } from 'lucide-react';
import { Settings, Users, Plus, Layers, X, Check } from '../components/icons';

export default function AdminCategoriasTicket() {
    const { user } = useAuth();
    const { alertSuccess, alertError } = useNotificationContext();
    const [categorias, setCategorias] = useState([]);
    const [soportes, setSoportes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('categorias');
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState(''); // 'categoria' o 'subcategoria'
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        prioridad: 'Media'
    });

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const [paramsCat, paramsUsers] = await Promise.all([
                categoriasTicketAPI.getCategorias(),
                authAPI.getUsuarios()
            ]);
            setCategorias(paramsCat.data);
            const soloSoportes = paramsUsers.data.filter(u => u.role === 'soporte');
            setSoportes(soloSoportes);
        } catch (err) {
            console.error('Error cargando datos:', err);
            alertError('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    // Verificar permisos de administrador
    if (user?.role !== 'admin') {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h2 className="text-lg font-semibold text-red-800">Acceso Denegado</h2>
                    <p className="text-red-600">Solo los administradores pueden acceder a esta página.</p>
                </div>
            </div>
        );
    }

    const cargarCategorias = async () => {
        try {
            const response = await categoriasTicketAPI.getCategorias();
            setCategorias(response.data);
        } catch (err) {
            console.error('Error cargando categorías:', err);
            alertError('Error al cargar las categorías');
        }
    };

    const cargarSoportes = async () => {
        try {
            const response = await authAPI.getUsuarios();
            setSoportes(response.data.filter(u => u.role === 'soporte'));
        } catch (err) {
            console.error('Error cargando soportes:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (modalType === 'categoria') {
                // CategoriaTicket no tiene campo prioridad/categoriaId -- el backend
                // rechaza (400) cualquier campo no declarado en el DTO, así que solo
                // se envían los campos que este modelo realmente acepta.
                const payload = { nombre: formData.nombre, descripcion: formData.descripcion };
                if (editingItem) {
                    await categoriasTicketAPI.updateCategoria(editingItem.id, payload);
                    alertSuccess('Categoría actualizada exitosamente');
                } else {
                    await categoriasTicketAPI.createCategoria(payload);
                    alertSuccess('Categoría creada exitosamente');
                }
            } else if (modalType === 'subcategoria') {
                const payload = { nombre: formData.nombre, descripcion: formData.descripcion, prioridad: formData.prioridad };
                if (editingItem) {
                    await categoriasTicketAPI.updateSubcategoria(editingItem.id, payload);
                    alertSuccess('Subcategoría actualizada exitosamente');
                } else {
                    const categoriaId = editingItem?.categoriaId || formData.categoriaId;
                    await categoriasTicketAPI.createSubcategoria(categoriaId, payload);
                    alertSuccess('Subcategoría creada exitosamente');
                }
            }
            cargarCategorias();
            cerrarModal();
        } catch (err) {
            console.error('Error guardando:', err);
            alertError('Error al guardar los datos');
        }
    };

    const handleDelete = async (id, tipo) => {
        if (!window.confirm(`¿Estás seguro de que quieres eliminar esta ${tipo}?`)) return;

        try {
            if (tipo === 'categoria') {
                await categoriasTicketAPI.deleteCategoria(id);
                alertSuccess('Categoría eliminada exitosamente');
            } else {
                await categoriasTicketAPI.deleteSubcategoria(id);
                alertSuccess('Subcategoría eliminada exitosamente');
            }
            cargarCategorias();
        } catch (err) {
            console.error('Error eliminando:', err);
            alertError('Error al eliminar el elemento');
        }
    };

    const abrirModal = (tipo, item = null, categoriaId = null) => {
        setModalType(tipo);
        setEditingItem(item);
        setFormData({
            nombre: item?.nombre || '',
            descripcion: item?.descripcion || '',
            prioridad: item?.prioridad || 'Media',
            categoriaId: categoriaId || item?.categoriaId || null
        });
        setShowModal(true);
    };

    const cerrarModal = () => {
        setShowModal(false);
        setEditingItem(null);
        setFormData({ nombre: '', descripcion: '', prioridad: 'Media' });
    };

    const updatePerfilSoporte = async (id, nuevoPerfil) => {
        try {
            await authAPI.updateUser(id, { soportePerfil: nuevoPerfil });
            alertSuccess('Perfil actualizado exitosamente');
            cargarSoportes();
        } catch (error) {
            console.error('Error al actualizar perfil', error);
            alertError('Error al actualizar perfil de soporte');
        }
    };

    if (loading) {
        return (
            <div className="p-6">
                <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                    <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6 max-w-7xl mx-auto">
            {/* Header y Tabs en una sola tarjeta premium */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
                <div className="p-6 sm:px-8 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0 border-b border-gray-100 dark:border-gray-700/50">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Administración de Tickets</h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Configuración general y asignación de técnicos a sus distintos perfiles de soporte.</p>
                    </div>
                    {activeTab === 'categorias' && (
                        <button
                            onClick={() => abrirModal('categoria')}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Nueva Categoría</span>
                        </button>
                    )}
                </div>

                <div className="px-6 sm:px-8 bg-gray-50/50 dark:bg-gray-800/50">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('categorias')}
                            className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'categorias'
                                ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                        >
                            <Settings className="h-4 w-4" />
                            <span>Categorías y Prioridades</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('perfiles')}
                            className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'perfiles'
                                ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                        >
                            <Users className="h-4 w-4" />
                            <span>Perfiles de Soporte</span>
                        </button>
                    </nav>
                </div>
            </div>

            {/* Contenido de Tab Categorias */}
            {activeTab === 'categorias' && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    {categorias.map((categoria) => (
                        <div key={categoria.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col group">

                            {/* Card Header */}
                            <div className="p-6 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/30 dark:bg-gray-800/30 flex justify-between items-start">
                                <div className="pr-4">
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                                        <Layers className="h-5 w-5 mr-2 text-blue-500" />
                                        {categoria.nombre}
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{categoria.descripcion}</p>
                                </div>

                                <div className="flex space-x-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={() => abrirModal('categoria', categoria)}
                                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                                        title="Editar Categoría"
                                    >
                                        <Edit2 className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(categoria.id, 'categoria')}
                                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                                        title="Eliminar Categoría"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Card Body - Subcategorías */}
                            <div className="p-6 flex-1 bg-white dark:bg-gray-800">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider flex items-center">
                                        <Tag className="h-4 w-4 mr-1.5 text-gray-400" />
                                        Subcategorías
                                    </h4>
                                    <button
                                        onClick={() => abrirModal('subcategoria', null, categoria.id)}
                                        className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center"
                                    >
                                        <Plus className="h-3 w-3 mr-1" /> Nuevo Tema
                                    </button>
                                </div>

                                {categoria.subcategorias && categoria.subcategorias.length > 0 ? (
                                    <ul className="space-y-3">
                                        {categoria.subcategorias.map((subcategoria) => (
                                            <li key={subcategoria.id} className="group/item flex justify-between items-start p-3 rounded-lg border border-transparent hover:border-gray-200 dark:hover:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                                <div>
                                                    <div className="flex items-center">
                                                        <span className="font-medium text-sm text-gray-900 dark:text-gray-100">{subcategoria.nombre}</span>
                                                        <span className={`ml-2 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${subcategoria.prioridad === 'Baja' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                            subcategoria.prioridad === 'Media' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                                                subcategoria.prioridad === 'Alta' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                                                    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                                            }`}>
                                                            {subcategoria.prioridad}
                                                        </span>
                                                    </div>
                                                    {subcategoria.descripcion && (
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subcategoria.descripcion}</p>
                                                    )}
                                                </div>

                                                <div className="flex space-x-1 opacity-100 sm:opacity-0 sm:group-hover/item:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={() => abrirModal('subcategoria', subcategoria)}
                                                        className="p-1 text-gray-400 hover:text-blue-600 rounded transition-colors"
                                                        title="Editar Subcategoría"
                                                    >
                                                        <Edit2 className="h-3.5 w-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(subcategoria.id, 'subcategoria')}
                                                        className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                                                        title="Eliminar Subcategoría"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-6 text-center">
                                        <div className="bg-gray-50 dark:bg-gray-800 rounded-full p-3 mb-2">
                                            <Tag className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Aún no hay subcategorías.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Contenido de Tab Perfiles */}
            {activeTab === 'perfiles' && (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700bg-gray-50 dark:bg-gray-800">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                            <Shield className="h-5 w-5 mr-2 text-blue-500" />
                            Asignación de Perfiles a Técnicos
                        </h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Define qué técnicos pueden atender tickets regulares (Soporte General) y quiénes están dedicados exclusivamente a sistemas contables y ERPs (Soporte SAP).
                        </p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900/50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Técnico</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Perfil de Soporte</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Acción</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {soportes.map((soporte) => (
                                    <tr key={soporte.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">{soporte.nombre} {soporte.apellido}</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">{soporte.cargo || 'Técnico de Soporte'}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {soporte.email}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${soporte.soportePerfil === 'SAP' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'}`}>
                                                {soporte.soportePerfil === 'SAP' ? 'Soporte SAP' : 'Soporte General'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <select
                                                value={soporte.soportePerfil || 'General'}
                                                onChange={(e) => updatePerfilSoporte(soporte.id, e.target.value)}
                                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                            >
                                                <option value="General">General</option>
                                                <option value="SAP">SAP</option>
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                                {soportes.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                                            No se encontraron técnicos de soporte.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            {editingItem ? 'Editar' : 'Nueva'} {modalType === 'categoria' ? 'Categoría' : 'Subcategoría'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Nombre *
                                </label>
                                <input
                                    type="text"
                                    value={formData.nombre}
                                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Descripción
                                </label>
                                <textarea
                                    value={formData.descripcion}
                                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    rows="3"
                                />
                            </div>
                            {modalType === 'subcategoria' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Prioridad *
                                    </label>
                                    <select
                                        value={formData.prioridad}
                                        onChange={(e) => setFormData({ ...formData, prioridad: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        required
                                    >
                                        <option value="Baja">Baja</option>
                                        <option value="Media">Media</option>
                                        <option value="Alta">Alta</option>
                                        <option value="Crítica">Crítica</option>
                                    </select>
                                </div>
                            )}
                            <div className="flex justify-end space-x-2">
                                <button
                                    type="button"
                                    onClick={cerrarModal}
                                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    {editingItem ? 'Actualizar' : 'Crear'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}